import { faShareSquare } from '@fortawesome/free-solid-svg-icons';
import {
  faArrowSquareLeft,
  faArrowSquareRight
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Page from 'components/layout/Page';
import BlogCta from 'components/marketingSite/blog/BlogCta';
import BlogRegisterCta from 'components/marketingSite/blog/BlogRegisterCta';
import Badge from 'components/shared/Badge.styled';
import { Button } from 'components/shared/Button';
import { Heading1 } from 'components/shared/Typography';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { SlugsContext } from 'context/slugs/slugs.context';
import { useI18n } from 'hooks/useI18n';
import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { WP_NextPrev } from 'pages/blog/[slug]';
import { useContext, useEffect, useState } from 'react';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import {
  WP_REST_API_Attachment,
  WP_REST_API_Categories,
  WP_REST_API_Post,
  WP_REST_API_User
} from 'wp-types';
import BlogFloatingCta from './BlogFloatingCta';
import {
  StyledContainer,
  StyledFloatingBannerContainer,
  StyledPost,
  StyledPostContent,
  StyledPostHeader,
  StyledPostHero,
  StyledPostInfo,
  StyledPostInfoFooter,
  StyledPostNav,
  StyledPostNavHint,
  StyledPostNavItem,
  StyledPostNavItemButton,
  StyledPostNavItemTop,
  StyledPostOneLang,
  StyledPostTags
} from './BlogPostComponent.styled';
import BlogShareModal from './BlogShareModal';

type BlogPostComponentProps = {
  disqusShortname?: string;
  post: WP_REST_API_Post & {
    next: WP_NextPrev | null;
    previous: WP_NextPrev | null;
  };
  categories: WP_REST_API_Categories;
};

function getPostAuthor(post: WP_REST_API_Post): string | undefined {
  const embedded = post._embedded?.author;
  const author =
    embedded && embedded[0] ? (embedded[0] as WP_REST_API_User) : undefined;

  return author ? author.name : undefined;
}

function getPostThumb(post: WP_REST_API_Post): string | undefined {
  const embedded = post._embedded?.['wp:featuredmedia'];
  const media =
    embedded && embedded[0]
      ? (embedded[0] as WP_REST_API_Attachment)
      : undefined;

  return media ? media.source_url : undefined;
}

const BlogPostComponent: React.FC<BlogPostComponentProps> = ({
  disqusShortname = '',
  post,
  categories
}) => {
  const [shareModalIsOpen, setIsOpen] = useState(false);
  const { t } = useI18n('blog');
  const { locale } = useRouter();
  const { user, loadingUser } = useContext(LoggedUserContext);
  const { slug, hasSlugForLang } = useContext(SlugsContext);

  const [origin, setOrigin] = useState('');

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      // @ts-expect-error don't know why, but I cannot use `domNode instanceof Element`, as stated here: https://www.npmjs.com/package/html-react-parser#migration
      if (domNode.attribs?.class === 'signup-banner') {
        return <>{!user && !loadingUser && <BlogRegisterCta />}</>;
      }
    }
  };

  const postContent = parse(post.content.rendered, options);

  function openShareModal(): void {
    setIsOpen(true);
  }

  function closeShareModal(): void {
    setIsOpen(false);
  }

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  return (
    <Page hasScrollTop>
      <StyledContainer>
        <StyledPost>
          <StyledPostHeader>
            <Heading1 editorial>{post.title.rendered}</Heading1>
            <StyledPostInfo>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('post.published', {
                    date: new Date(post.date).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }),
                    author: getPostAuthor(post)
                  })
                }}
              ></span>

              <Button sizes="sm" variant="tertiary" onClick={openShareModal}>
                <FontAwesomeIcon icon={faShareSquare} />
                <span>{t('post.share')}</span>
              </Button>
            </StyledPostInfo>

            {!!slug && !hasSlugForLang(locale) && (
              <StyledPostOneLang>
                <span>
                  {locale === 'en' ? t('onlyLang_es') : t('onlyLang_en')}
                </span>
              </StyledPostOneLang>
            )}
          </StyledPostHeader>
          {getPostThumb(post) && (
            <StyledPostHero src={getPostThumb(post)} alt="" />
          )}
          <StyledPostContent>{postContent}</StyledPostContent>
          <StyledPostInfoFooter>
            <span
              dangerouslySetInnerHTML={{
                __html: t('post.published', {
                  date: new Date(post.date).toLocaleDateString(locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }),
                  author: getPostAuthor(post)
                })
              }}
            ></span>
          </StyledPostInfoFooter>
          <StyledPostTags>
            <strong>{t('post.tags')}:</strong>{' '}
            {categories
              ?.filter(
                (c) =>
                  c.slug !== 'uncategorised' &&
                  c.slug !== 'oculto' &&
                  c.slug !== 'sin-categorizar' &&
                  c.slug !== 'all' &&
                  c.slug !== 'todo-el-blog'
              )
              .map((category) => (
                <Link
                  key={category.id}
                  href={`${getAppUrl(APP_URLS.blog, locale)}/category/${
                    category.slug
                  }`}
                  passHref
                >
                  <Badge as="a" variant="secondary">
                    {category.name}
                  </Badge>
                </Link>
              ))}
          </StyledPostTags>

          {(post.previous || post.next) && (
            <StyledPostNav>
              {post.previous && (
                <Link
                  href={`${getAppUrl(APP_URLS.blog, locale)}/${
                    post.previous?.slug
                  }`}
                  passHref
                >
                  <StyledPostNavItem>
                    <StyledPostNavItemTop>
                      <StyledPostNavItemButton>
                        <FontAwesomeIcon
                          aria-hidden="true"
                          icon={faArrowSquareLeft}
                        />
                      </StyledPostNavItemButton>
                      <StyledPostNavHint>{t('post.prev')}</StyledPostNavHint>
                    </StyledPostNavItemTop>
                    {post.previous?.title}
                  </StyledPostNavItem>
                </Link>
              )}

              {post.next && (
                <Link
                  href={`${getAppUrl(APP_URLS.blog, locale)}/${
                    post.next?.slug
                  }`}
                  passHref
                >
                  <StyledPostNavItem className="next">
                    <StyledPostNavItemTop>
                      <StyledPostNavHint>{t('post.next')}</StyledPostNavHint>
                      <StyledPostNavItemButton>
                        <FontAwesomeIcon
                          aria-hidden="true"
                          icon={faArrowSquareRight}
                        />
                      </StyledPostNavItemButton>
                    </StyledPostNavItemTop>
                    {post.next?.title}
                  </StyledPostNavItem>
                </Link>
              )}
            </StyledPostNav>
          )}

          <BlogCta />
        </StyledPost>
        <StyledFloatingBannerContainer>
          <BlogFloatingCta />
        </StyledFloatingBannerContainer>
      </StyledContainer>
      <BlogShareModal
        slug={post.slug}
        shareModalIsOpen={shareModalIsOpen}
        shareModalClose={closeShareModal}
      />
    </Page>
  );
};

export default BlogPostComponent;
